import type { SubscriptionPeriod, SubscriptionTier, SubscriptionType } from "../types";
import type { Modify } from "../utils/typeUtils";

export const TeamsUserRoles = {
  Owner: "owner",
  Editor: "editor",
  SeatManager: "seat_manager",
  Contributor: "contributor",
  Guest: "guest",
} as const;

export type TeamsUserRole = (typeof TeamsUserRoles)[keyof typeof TeamsUserRoles];

export const NonSeatConsumingTeamsUserRoles: ReadonlyArray<TeamsUserRole> = [
  TeamsUserRoles.Contributor,
  TeamsUserRoles.Guest,
] as const;

export const TeamsUserRolesDisplayNames: { [key in TeamsUserRole]: string } = {
  owner: "Owner",
  editor: "Editor",
  seat_manager: "Admin",
  contributor: "Contributor",
  guest: "Guest",
} as const;

export type TeamAddons = {
  nxImporter?: boolean;
  syncForbidden?: boolean;
  privateCloud?: boolean;
  immersiveSpace?: boolean;
  teamspaceManagement?: boolean;
};

export type SubscriptionBadge =
  | "guest"
  | "basic"
  | "edu"
  | "pro"
  | "custom"
  | "studio"
  | "individual";

export type Team = {
  id: string;
  name: string;
  role: TeamsUserRole;
  isCreatedForAccount: boolean;
  isPrimary: boolean;
  subscriptionBadge: SubscriptionBadge;
  memberCount?: number;
  addons?: TeamAddons;
  isSalesTeam?: boolean;
};

export type LinkedPersonalAccount = {
  email: string;
  giftExpirationDate: string;
  giftIsActive: boolean;
};

export type AccountResponseData = {
  id: string;
  email: string;
  accountTeamID?: string;
  emailVerified: boolean;
  subscriptions: RawSubscription[];
  teamSubscription?: TeamSubscriptionInfo;
  availablePlans: AvailablePlan[];
  countryCode: string;
  name?: string;
  teams: Team[];
  flags?: Record<string, boolean>;
  ssoEnforced: boolean;
  twoFactorTotpEnabled: boolean;
  linkedPersonalAccount?: LinkedPersonalAccount;
};

export type TeamSubscriptionInfo = {
  id: string;
  name: string;
  seats?: number;
  purchaseDate?: string;
  expirationDate?: string;
};

export type Plan = {
  payLink?: string;
};

export type AvailablePlan = {
  tier: string;
  period: SubscriptionPeriod;
  trialDays: number;
};

type RawSubscription = {
  id?: number;
  teamSubscriptionId?: string; // For mapped chargebee subscriptions
  type: SubscriptionType;
  expiration: string;
  autorenewal: boolean;
  isTrial: boolean;
  subscriptionPeriod: SubscriptionPeriod;
  paddleId?: string;
  tier: SubscriptionTier;
  gracePeriodExpirationDate?: string;
  isAutoRenewEditable?: boolean;
  isOnlyCancelable?: boolean;
};

export type Subscription = Modify<
  RawSubscription,
  { expiration: Date; gracePeriodExpirationDate?: Date; teamId?: string; teamName?: string }
>;

export enum UserError {
  InvalidConfirmationHash,
  Unknown,
}

export type AccountData = Modify<
  AccountResponseData,
  {
    teamSubscription?: never;
    subscriptions: Subscription[];
  }
> & {
  hasPaddleSubscription: boolean;
  hasPaddleOneTimeSubscription: boolean;
  hasChargebeeSubscription: boolean;
  hasAppStoreSubscription: boolean;
  subscriptionInGracePeriod: Subscription | undefined;
  canPurchaseSubscription: () => boolean;
  techTrialAvailable: boolean;
  salesTeams: Team[];
  isInSalesTeam: boolean;
  showPersonalAccountSection: boolean;
  canLinkPersonalAccount: boolean;
};
